import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AppProvider, useApp } from './context/AppContext';
import Layout from './components/Layout';
import LayoutProduccion from './components/produccion/LayoutProduccion';
import LayoutMuestras from './components/muestras/LayoutMuestras';

// Pages - General
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import GestionUsuariosPage from './pages/GestionUsuariosPage';

// Pages - Finanzas
import Dashboard from './pages/Dashboard';
import ComprasPage from './pages/ComprasPage';
import CompraFormPage from './pages/CompraFormPage';
import PagarFacturasPage from './pages/PagarFacturasPage';
import CxPPage from './pages/CxPPage';
import LetrasPage from './pages/LetrasPage';
import CuentasPage from './pages/CuentasPage';
import PagosPage from './pages/PagosPage';
import EmpresasPage from './pages/EmpresasPage';
import ProveedoresPage from './pages/ProveedoresPage';
import ClientesPage from './pages/ClientesPage';
import ArticulosPage from './pages/ArticulosPage';
import CategoriasPage from './pages/CategoriasPage';
import LineasNegocioPage from './pages/LineasNegocioPage';
import CentrosCostoPage from './pages/CentrosCostoPage';
import ReportesPage from './pages/ReportesPage';
import EmpleadosPage from './pages/EmpleadosPage';
import ConceptosPlanillaPage from './pages/ConceptosPlanillaPage';
import PlanillaPage from './pages/PlanillaPage';
import AdelantosPage from './pages/AdelantosPage';
import ConciliacionPage from './pages/ConciliacionPage';
import HistorialConciliacionesPage from './pages/HistorialConciliacionesPage';
import VentasPage from './pages/VentasPage';
import OrdenesCompraPage from './pages/OrdenesCompraPage';
import OrdenCompraFormPage from './pages/OrdenCompraFormPage';
import CreditosPage from './pages/CreditosPage';
import UsuariosPage from './pages/UsuariosPage';
import HistorialActividadPage from './pages/HistorialActividadPage';
import PresupuestosPage from './pages/PresupuestosPage';
import BackupsPage from './pages/BackupsPage';
import IntegracionPage from './pages/IntegracionPage';
import CuentasContablesPage from './pages/CuentasContablesPage';
import BalanceGeneralPage from './pages/BalanceGeneralPage';

// Pages - Producción
import ProdDashboard from './pages/produccion/Dashboard';
import ProdMarcas from './pages/produccion/Marcas';
import ProdTipos from './pages/produccion/Tipos';
import ProdEntalles from './pages/produccion/Entalles';
import ProdTelas from './pages/produccion/Telas';
import ProdHilos from './pages/produccion/Hilos';
import ProdHilosEspecificos from './pages/produccion/HilosEspecificos';
import ProdTallasCatalogo from './pages/produccion/TallasCatalogo';
import ProdColoresCatalogo from './pages/produccion/ColoresCatalogo';
import ProdColoresGenerales from './pages/produccion/ColoresGenerales';
import ProdModelos from './pages/produccion/Modelos';
import ProdRegistros from './pages/produccion/Registros';
import ProdInventario from './pages/produccion/Inventario';
import ProdInventarioIngresos from './pages/produccion/InventarioIngresos';
import ProdInventarioSalidas from './pages/produccion/InventarioSalidas';
import ProdInventarioAjustes from './pages/produccion/InventarioAjustes';
import ProdInventarioRollos from './pages/produccion/InventarioRollos';
import ProdServiciosProduccion from './pages/produccion/ServiciosProduccion';
import ProdPersonasProduccion from './pages/produccion/PersonasProduccion';
import ProdRutasProduccion from './pages/produccion/RutasProduccion';
import ProdMovimientosProduccion from './pages/produccion/MovimientosProduccion';
import ProdGuiasRemision from './pages/produccion/GuiasRemision';
import ProdCalidadMerma from './pages/produccion/CalidadMerma';
import ProdHistorial from './pages/produccion/HistorialActividad';
import ProdRegistroForm from './pages/produccion/RegistroForm';
import { Kardex as ProdKardex } from './pages/produccion/Kardex';
import ProdPendientesRecibir from './pages/produccion/PendientesRecibir';
import ProdRecepcionesCompras from './pages/produccion/RecepcionesCompras';

// Pages - Muestras
import MuestrasDashboard from './pages/muestras/MuestrasDashboard';
import MuestrasTelas from './pages/muestras/MuestrasTelas';
import MuestrasHilos from './pages/muestras/MuestrasHilos';
import MuestrasEntalles from './pages/muestras/MuestrasEntalles';
import MuestrasMarcas from './pages/muestras/MuestrasMarcas';
import MuestrasBases from './pages/muestras/MuestrasBases';
import MuestrasBase from './pages/muestras/MuestrasBase';
import MuestrasTizados from './pages/muestras/MuestrasTizados';
import MuestrasTipos from './pages/muestras/MuestrasTipos';
import MuestrasUsuarios from './pages/muestras/MuestrasUsuarios';
import MuestrasHistorial from './pages/muestras/MuestrasHistorial';
import MuestrasFichas from './pages/muestras/MuestrasFichas';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useApp();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout wrapper for Finanzas pages
const WithLayout = ({ children }) => (
  <ProtectedRoute>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

// Layout wrapper for Producción pages
const WithLayoutProduccion = ({ children }) => (
  <ProtectedRoute>
    <LayoutProduccion>{children}</LayoutProduccion>
  </ProtectedRoute>
);

// Full screen pages (no layout but protected)
const FullScreenProtected = ({ children }) => (
  <ProtectedRoute>
    {children}
  </ProtectedRoute>
);

// Muestras Protected Route - now uses main auth system
const MuestrasProtectedRoute = ({ children }) => {
  const { isAuthenticated, user } = useApp();
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Wait for user data to load
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando...</p>
        </div>
      </div>
    );
  }
  
  // Check access to Muestras module - Admin has access to all
  const hasAccess = user.rol === 'ADMIN' || user.acceso_muestras;
  
  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Layout wrapper for Muestras - uses main auth
const WithLayoutMuestras = ({ children }) => (
  <MuestrasProtectedRoute>
    <LayoutMuestras>{children}</LayoutMuestras>
  </MuestrasProtectedRoute>
);

// Login route - redirect if already authenticated
const LoginRoute = () => {
  const { isAuthenticated } = useApp();
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <LoginPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginRoute />} />
      
      {/* Landing Page */}
      <Route path="/" element={<ProtectedRoute><LandingPage /></ProtectedRoute>} />
      
      {/* User Management (Admin only) */}
      <Route path="/usuarios" element={<ProtectedRoute><GestionUsuariosPage /></ProtectedRoute>} />
      
      {/* ============================================== */}
      {/* FINANZAS ROUTES */}
      {/* ============================================== */}
      
      {/* Full screen forms (protected) */}
      <Route path="/finanzas/compras/nuevo" element={<FullScreenProtected><CompraFormPage /></FullScreenProtected>} />
      <Route path="/finanzas/compras/:id/editar" element={<FullScreenProtected><CompraFormPage /></FullScreenProtected>} />
      <Route path="/finanzas/ordenes-compra/nueva" element={<FullScreenProtected><OrdenCompraFormPage /></FullScreenProtected>} />
      <Route path="/finanzas/ordenes-compra/:id" element={<FullScreenProtected><OrdenCompraFormPage /></FullScreenProtected>} />
      
      {/* Dashboard */}
      <Route path="/finanzas" element={<WithLayout><Dashboard /></WithLayout>} />
      
      {/* Compras */}
      <Route path="/finanzas/ordenes-compra" element={<WithLayout><OrdenesCompraPage /></WithLayout>} />
      <Route path="/finanzas/compras/factura" element={<WithLayout><ComprasPage tipo="FACTURA_PROVEEDOR" /></WithLayout>} />
      <Route path="/finanzas/compras/gasto" element={<WithLayout><ComprasPage tipo="GASTO" /></WithLayout>} />
      <Route path="/finanzas/pagar-facturas" element={<WithLayout><PagarFacturasPage /></WithLayout>} />
      <Route path="/finanzas/cxp" element={<WithLayout><CxPPage /></WithLayout>} />
      <Route path="/finanzas/letras" element={<WithLayout><LetrasPage /></WithLayout>} />
      
      {/* Ventas */}
      <Route path="/finanzas/ventas" element={<WithLayout><VentasPage /></WithLayout>} />
      <Route path="/finanzas/creditos" element={<WithLayout><CreditosPage /></WithLayout>} />
      <Route path="/finanzas/presupuestos" element={<WithLayout><PresupuestosPage /></WithLayout>} />
      
      {/* Bancos */}
      <Route path="/finanzas/cuentas" element={<WithLayout><CuentasPage /></WithLayout>} />
      <Route path="/finanzas/pagos" element={<WithLayout><PagosPage /></WithLayout>} />
      <Route path="/finanzas/conciliacion" element={<WithLayout><ConciliacionPage /></WithLayout>} />
      <Route path="/finanzas/historial-conciliaciones" element={<WithLayout><HistorialConciliacionesPage /></WithLayout>} />
      
      {/* Catálogos */}
      <Route path="/finanzas/empresas" element={<WithLayout><EmpresasPage /></WithLayout>} />
      <Route path="/finanzas/proveedores" element={<WithLayout><ProveedoresPage /></WithLayout>} />
      <Route path="/finanzas/clientes" element={<WithLayout><ClientesPage /></WithLayout>} />
      <Route path="/finanzas/articulos" element={<WithLayout><ArticulosPage /></WithLayout>} />
      <Route path="/finanzas/categorias" element={<WithLayout><CategoriasPage /></WithLayout>} />
      <Route path="/finanzas/lineas-negocio" element={<WithLayout><LineasNegocioPage /></WithLayout>} />
      <Route path="/finanzas/centros-costo" element={<WithLayout><CentrosCostoPage /></WithLayout>} />
      
      {/* Planilla */}
      <Route path="/finanzas/empleados" element={<WithLayout><EmpleadosPage /></WithLayout>} />
      <Route path="/finanzas/conceptos-planilla" element={<WithLayout><ConceptosPlanillaPage /></WithLayout>} />
      <Route path="/finanzas/adelantos" element={<WithLayout><AdelantosPage /></WithLayout>} />
      <Route path="/finanzas/planilla" element={<WithLayout><PlanillaPage /></WithLayout>} />
      
      {/* Reportes */}
      <Route path="/finanzas/reportes" element={<WithLayout><ReportesPage /></WithLayout>} />
      
      {/* Contabilidad */}
      <Route path="/finanzas/cuentas-contables" element={<WithLayout><CuentasContablesPage /></WithLayout>} />
      <Route path="/finanzas/balance-general" element={<WithLayout><BalanceGeneralPage /></WithLayout>} />
      
      {/* Admin */}
      <Route path="/finanzas/usuarios" element={<WithLayout><UsuariosPage /></WithLayout>} />
      <Route path="/finanzas/historial-actividad" element={<WithLayout><HistorialActividadPage /></WithLayout>} />
      <Route path="/finanzas/backups" element={<WithLayout><BackupsPage /></WithLayout>} />
      <Route path="/finanzas/integracion" element={<WithLayout><IntegracionPage /></WithLayout>} />
      
      {/* ============================================== */}
      {/* PRODUCCIÓN ROUTES */}
      {/* ============================================== */}
      
      <Route path="/produccion" element={<WithLayoutProduccion><ProdDashboard /></WithLayoutProduccion>} />
      <Route path="/produccion/marcas" element={<WithLayoutProduccion><ProdMarcas /></WithLayoutProduccion>} />
      <Route path="/produccion/tipos" element={<WithLayoutProduccion><ProdTipos /></WithLayoutProduccion>} />
      <Route path="/produccion/entalles" element={<WithLayoutProduccion><ProdEntalles /></WithLayoutProduccion>} />
      <Route path="/produccion/telas" element={<WithLayoutProduccion><ProdTelas /></WithLayoutProduccion>} />
      <Route path="/produccion/hilos" element={<WithLayoutProduccion><ProdHilos /></WithLayoutProduccion>} />
      <Route path="/produccion/hilos-especificos" element={<WithLayoutProduccion><ProdHilosEspecificos /></WithLayoutProduccion>} />
      <Route path="/produccion/tallas-catalogo" element={<WithLayoutProduccion><ProdTallasCatalogo /></WithLayoutProduccion>} />
      <Route path="/produccion/colores-catalogo" element={<WithLayoutProduccion><ProdColoresCatalogo /></WithLayoutProduccion>} />
      <Route path="/produccion/colores-generales" element={<WithLayoutProduccion><ProdColoresGenerales /></WithLayoutProduccion>} />
      <Route path="/produccion/modelos" element={<WithLayoutProduccion><ProdModelos /></WithLayoutProduccion>} />
      <Route path="/produccion/registros" element={<WithLayoutProduccion><ProdRegistros /></WithLayoutProduccion>} />
      <Route path="/produccion/registros/nuevo" element={<WithLayoutProduccion><ProdRegistroForm /></WithLayoutProduccion>} />
      <Route path="/produccion/registros/editar/:id" element={<WithLayoutProduccion><ProdRegistroForm /></WithLayoutProduccion>} />
      
      {/* Inventario */}
      <Route path="/produccion/inventario" element={<WithLayoutProduccion><ProdInventario /></WithLayoutProduccion>} />
      <Route path="/produccion/inventario/ingresos" element={<WithLayoutProduccion><ProdInventarioIngresos /></WithLayoutProduccion>} />
      <Route path="/produccion/inventario/salidas" element={<WithLayoutProduccion><ProdInventarioSalidas /></WithLayoutProduccion>} />
      <Route path="/produccion/inventario/ajustes" element={<WithLayoutProduccion><ProdInventarioAjustes /></WithLayoutProduccion>} />
      <Route path="/produccion/inventario/rollos" element={<WithLayoutProduccion><ProdInventarioRollos /></WithLayoutProduccion>} />
      <Route path="/produccion/inventario/kardex" element={<WithLayoutProduccion><ProdKardex /></WithLayoutProduccion>} />
      <Route path="/produccion/inventario/pendientes" element={<WithLayoutProduccion><ProdPendientesRecibir /></WithLayoutProduccion>} />
      <Route path="/produccion/inventario/recepciones" element={<WithLayoutProduccion><ProdRecepcionesCompras /></WithLayoutProduccion>} />
      
      {/* Maestros */}
      <Route path="/produccion/maestros/servicios" element={<WithLayoutProduccion><ProdServiciosProduccion /></WithLayoutProduccion>} />
      <Route path="/produccion/maestros/personas" element={<WithLayoutProduccion><ProdPersonasProduccion /></WithLayoutProduccion>} />
      <Route path="/produccion/maestros/rutas" element={<WithLayoutProduccion><ProdRutasProduccion /></WithLayoutProduccion>} />
      <Route path="/produccion/maestros/movimientos" element={<WithLayoutProduccion><ProdMovimientosProduccion /></WithLayoutProduccion>} />
      
      {/* Otros */}
      <Route path="/produccion/guias-remision" element={<WithLayoutProduccion><ProdGuiasRemision /></WithLayoutProduccion>} />
      <Route path="/produccion/calidad-merma" element={<WithLayoutProduccion><ProdCalidadMerma /></WithLayoutProduccion>} />
      <Route path="/produccion/historial" element={<WithLayoutProduccion><ProdHistorial /></WithLayoutProduccion>} />
      <Route path="/produccion/integracion" element={<WithLayoutProduccion><IntegracionPage /></WithLayoutProduccion>} />
      
      {/* ============================================== */}
      {/* MUESTRAS ROUTES - Usa autenticación global */}
      {/* ============================================== */}
      
      <Route path="/muestras" element={<Navigate to="/muestras/dashboard" replace />} />
      <Route path="/muestras/dashboard" element={<WithLayoutMuestras><MuestrasDashboard /></WithLayoutMuestras>} />
      <Route path="/muestras/bases" element={<WithLayoutMuestras><MuestrasBases /></WithLayoutMuestras>} />
      <Route path="/muestras/muestra-base" element={<WithLayoutMuestras><MuestrasBase /></WithLayoutMuestras>} />
      <Route path="/muestras/telas" element={<WithLayoutMuestras><MuestrasTelas /></WithLayoutMuestras>} />
      <Route path="/muestras/hilos" element={<WithLayoutMuestras><MuestrasHilos /></WithLayoutMuestras>} />
      <Route path="/muestras/entalles" element={<WithLayoutMuestras><MuestrasEntalles /></WithLayoutMuestras>} />
      <Route path="/muestras/marcas" element={<WithLayoutMuestras><MuestrasMarcas /></WithLayoutMuestras>} />
      <Route path="/muestras/tizados" element={<WithLayoutMuestras><MuestrasTizados /></WithLayoutMuestras>} />
      <Route path="/muestras/tipos" element={<WithLayoutMuestras><MuestrasTipos /></WithLayoutMuestras>} />
      <Route path="/muestras/fichas" element={<WithLayoutMuestras><MuestrasFichas /></WithLayoutMuestras>} />
      <Route path="/muestras/usuarios" element={<WithLayoutMuestras><MuestrasUsuarios /></WithLayoutMuestras>} />
      <Route path="/muestras/historial" element={<WithLayoutMuestras><MuestrasHistorial /></WithLayoutMuestras>} />
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <Router>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </Router>
    </AppProvider>
  );
}

export default App;
